import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IMPORT_CONFIRMATION_PHRASE, type BackupSummary } from "@/lib/callables";
import { formatBytes } from "@/lib/dataTransfer";
import { formatCount } from "@/lib/format";

/**
 * A confirmação dupla da importação, em dois diálogos seguidos:
 *
 * 1. entender o que acontece (marcar "entendi" e, com arquivo de outro
 *    projeto, confirmar a troca de projeto);
 * 2. digitar a frase de confirmação, que o backend confere de novo.
 *
 * Quem usa monta o componente só enquanto a confirmação está aberta, e assim
 * cada importação começa com as marcações e a frase em branco.
 */
export function ImportConfirmation({
  fileName,
  summary,
  currentProjectId,
  otherProject,
  onCancel,
  onConfirm,
}: {
  fileName: string;
  summary: BackupSummary;
  currentProjectId: string;
  /** O projeto do arquivo, quando não é o do painel. */
  otherProject: string | null;
  onCancel: () => void;
  onConfirm: (input: { phrase: string; acceptProjectMismatch: boolean }) => void;
}) {
  const [stage, setStage] = useState<"first" | "second">("first");
  const [understood, setUnderstood] = useState(false);
  const [acceptMismatch, setAcceptMismatch] = useState(false);
  const [phrase, setPhrase] = useState("");

  const closeOnDismiss = (open: boolean) => {
    if (!open) onCancel();
  };

  return (
    <>
      <AlertDialog open={stage === "first"} onOpenChange={closeOnDismiss}>
        <AlertDialogContent>
          <AlertDialogTitle>Substituir o Firestore e o Storage?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Os documentos do Firestore e os arquivos do Storage de hoje são apagados e trocados
                pelos do arquivo <span className="font-semibold text-foreground">{fileName}</span>:{" "}
                {formatCount(summary.firestore.documents, "documento", "documentos")} e{" "}
                {formatCount(summary.storage.files, "arquivo", "arquivos")} (
                {formatBytes(summary.storage.bytes)}).
              </p>
              <p>
                Antes, o servidor gera um backup de tudo, que o painel baixa e que fica guardado no
                Storage. A coleção <span className="font-mono">_meta</span>, as contas de login e as
                pastas <span className="font-mono">backups/</span> e{" "}
                <span className="font-mono">imports/</span> do Storage não mudam.
              </p>
            </div>
          </AlertDialogDescription>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-2.5 has-[:checked]:border-destructive">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-destructive"
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
            />
            <span className="text-small">
              Entendi que os dados atuais do Firestore e do Storage serão apagados e substituídos
              pelos do arquivo.
            </span>
          </label>
          {otherProject ? (
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-state-warning/60 p-2.5">
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-destructive"
                checked={acceptMismatch}
                onChange={(e) => setAcceptMismatch(e.target.checked)}
              />
              <span className="text-small">
                O arquivo é do projeto {otherProject}, e quero importá-lo no {currentProjectId}{" "}
                mesmo assim.
              </span>
            </label>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {/* Botão comum, e não AlertDialogAction: a ação fecharia o diálogo e
                atropelaria a troca para a segunda confirmação. */}
            <Button
              variant="destructive"
              disabled={!understood || (otherProject != null && !acceptMismatch)}
              onClick={() => setStage("second")}
            >
              Continuar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={stage === "second"} onOpenChange={closeOnDismiss}>
        <AlertDialogContent>
          <AlertDialogTitle>Confirmação final</AlertDialogTitle>
          <AlertDialogDescription>
            Para importar, digite{" "}
            <span className="font-mono font-semibold text-foreground">
              {IMPORT_CONFIRMATION_PHRASE}
            </span>{" "}
            abaixo. O backup é gerado e baixado primeiro; a troca começa logo depois.
          </AlertDialogDescription>
          <Input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={IMPORT_CONFIRMATION_PHRASE}
            aria-label="Frase de confirmação"
            autoComplete="off"
            className="font-mono"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={phrase !== IMPORT_CONFIRMATION_PHRASE}
              onClick={() =>
                onConfirm({
                  phrase,
                  acceptProjectMismatch: otherProject != null && acceptMismatch,
                })
              }
            >
              Fazer o backup e importar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
